import { ChangeEvent, FC, KeyboardEvent, useState } from 'react';
import { useFormContext } from 'react-hook-form';
// i18next
import { useTranslation } from 'react-i18next';
// MUI
import { Box, Button, Chip, FormHelperText } from '@mui/material';
// Components
import { Input } from 'app/components/Mui';
// Utilities
import { isEmailValid } from 'app/utilities/Validations';

type Props = {
  label: string;
  helperText?: string;
}

interface IFormData {
  emails: string[];
}

const BillingEmails:FC<Props> = ({ label, helperText }) => {
  const { t } = useTranslation();

  const methods = useFormContext<IFormData>();

  const handleSetEmails = (newEmails:string[]) => {
    methods.setValue('emails', newEmails);
  }

  const watchEmails = methods.watch('emails') || [];

  const [ value, setValue ] = useState('');
  const [ error, setError ] = useState(false);

  const handleChange = (event:ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  }

  const handleAdd = () => {
    if ( !isEmailValid.value.test(value) ) {
      setError(true);
      return;
    };
    handleSetEmails([...watchEmails, value]);
    setValue('');
    setError(false);
  }

  const handleRemove = (idx:number) => () => {
    handleSetEmails(watchEmails.filter((_, index:number) => index !== idx));
  }

  const onKeyDown = (event:KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAdd();
    }
  }

  return (
    <Box  sx={{ pt: 4, mb: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Input
          sx={{ flexGrow: 1 }}
          margin="none"
          label={label}
          value={value}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          error={error}
          helperText={error ? isEmailValid.message : ''}
        />
        <Box sx={{ pt: '10px' }}>
          <Button
            disabled={!value}
            onClick={handleAdd}
            variant="contained"
            color="primary"
          >{t('buttons.add')}</Button>
        </Box>
      </Box>
      {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
      {watchEmails.length ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 2, gap: 2 }}>
          {watchEmails.map((email:string, index:number) => (
            <Chip
              key={`notification-email-item-${index}`}
              label={email}
              onDelete={handleRemove(index)}
            />
          ))}
        </Box>
      ) : null}
    </Box>
  )
}

export default BillingEmails;
